import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardIcon, ShieldCheckIcon, LockClosedIcon, UsersIcon } from '../components/icons';
import { useClipStore } from '../stores/clipStore';
import { useI18n } from '../i18n';
import LanguageSwitch from '../components/LanguageSwitch';

export default function HomePage() {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const createClip = useClipStore((state) => state.createClip);
  const { t, locale } = useI18n();

  const handleCreate = async () => {
    setCreating(true);
    try {
      const { uuid } = await createClip();
      navigate(`${locale === 'en' ? '/en' : ''}/c/${uuid}`);
    } catch (e) {
      alert(locale === 'zh' ? '创建失败，请重试' : 'Failed to create, please try again');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4">
      {/* Header with Language Switch */}
      <div className="absolute top-4 right-4">
        <LanguageSwitch />
      </div>

      {/* Main Content */}
      <div className="text-center mb-12">
        <div className="flex items-center justify-center mb-4">
          <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
            <ClipboardIcon className="w-8 h-8 text-white" />
          </div>
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-3">{t.home.title}</h1>
        <p className="text-lg text-gray-600 max-w-md mx-auto">
          {t.home.subtitle}
          <br />
          <span className="text-sm text-gray-500">{t.home.tagline}</span>
        </p>
      </div>

      {/* Create Button */}
      <button
        onClick={handleCreate}
        disabled={creating}
        className="group relative px-8 py-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5"
      >
        {creating ? (
          <span className="flex items-center gap-2">
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            {t.home.creating}
          </span>
        ) : (
          t.home.createButton
        )}
      </button>

      {/* Features */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-16 max-w-3xl">
        <FeatureCard
          icon={<LockClosedIcon className="w-6 h-6 text-green-600" />}
          title={t.home.features.encryption.title}
          description={t.home.features.encryption.desc}
        />
        <FeatureCard
          icon={<ShieldCheckIcon className="w-6 h-6 text-blue-600" />}
          title={t.home.features.zeroKnowledge.title}
          description={t.home.features.zeroKnowledge.desc}
        />
        <FeatureCard
          icon={<UsersIcon className="w-6 h-6 text-purple-600" />}
          title={t.home.features.realtime.title}
          description={t.home.features.realtime.desc}
        />
      </div>

      {/* Footer */}
      <footer className="mt-16 text-sm text-gray-500">
        {t.home.footer}
      </footer>
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col items-center text-center p-4">
      <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-3">
        {icon}
      </div>
      <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-sm text-gray-500">{description}</p>
    </div>
  );
}
